import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Login from '@mui/icons-material/Login';
import Alert from '@mui/material/Alert';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import TaskPhotoEvidencePanel from './TaskPhotoEvidencePanel';
import WorkerTaskCompleteOverlay from './WorkerTaskCompleteOverlay';
import useWorkerFieldData from './useWorkerFieldData';
import SitePlanMap from '../../components/siteplan/SitePlanMap';
import {
  WorkerPeriod,
  computeWorkerPerformance,
  dateInWorkerPeriod,
  formatWorkerDateLabel,
  taskResultDate,
  taskResultDateKey,
  taskSafetyProfile,
} from './workerFieldInsights';
import { canStartTask, normalizeStatus, selectCurrentWorkerTask } from './workerFieldModeUtils';
import type { AttendanceRecord } from '../../services/api/attendanceService';
import attendanceService from '../../services/api/attendanceService';
import type { DefectBoardRow } from '../../services/api/defectService';
import type { TaskMaterial } from '../../services/api/taskMaterialsService';
import tasksService, { Task } from '../../services/api/tasksService';
import { enqueueOfflineAttendance, enqueueOfflineTaskPatch } from '../../services/offline/fieldOfflineForms';
import { workerSpecificationItems } from '../../utils/taskRoleSpecifications';

type WorkerFieldView = 'home' | 'current' | 'upcoming' | 'performance' | 'emergency' | 'defects' | 'inspections';

interface WorkerFieldWorkspacePageProps {
  view: WorkerFieldView;
  workerId?: string | null;
  navigationBasePath?: string;
}

const DISPLAY_ROW_LIMIT = 3;

const CODE39_PATTERNS: Record<string, string> = {
  '0': 'nnnwwnwnn',
  '1': 'wnnwnnnnw',
  '2': 'nnwwnnnnw',
  '3': 'wnwwnnnnn',
  '4': 'nnnwwnnnw',
  '5': 'wnnwwnnnn',
  '6': 'nnwwwnnnn',
  '7': 'nnnwnnwnw',
  '8': 'wnnwnnwnn',
  '9': 'nnwwnnwnn',
  A: 'wnnnnwnnw',
  B: 'nnwnnwnnw',
  C: 'wnwnnwnnn',
  D: 'nnnnwwnnw',
  E: 'wnnnwwnnn',
  F: 'nnwnwwnnn',
  G: 'nnnnnwwnw',
  H: 'wnnnnwwnn',
  I: 'nnwnnwwnn',
  J: 'nnnnwwwnn',
  K: 'wnnnnnwnw',
  L: 'nnwnnnwnw',
  M: 'wnwnnnwnn',
  N: 'nnnnwnwnw',
  O: 'wnnnwnwnn',
  P: 'nnwnwnwnn',
  Q: 'nnnnnnwww',
  R: 'wnnnnnwwn',
  S: 'nnwnnnwwn',
  T: 'nnnnwnwwn',
  U: 'wwnnnnnnw',
  V: 'nwwnnnnnw',
  W: 'wwwnnnnnn',
  X: 'nwnnwnnnw',
  Y: 'wwnnwnnnn',
  Z: 'nwwnwnnnn',
  '-': 'nwnnnnwnw',
  '.': 'wwnnnnwnn',
  ' ': 'nwwnnnwnn',
  '$': 'nwnwnwnnn',
  '/': 'nwnwnnnwn',
  '+': 'nwnnnwnwn',
  '%': 'nnnwnwnwn',
  '*': 'nwnnwnwnn',
};

const formatDate = (value?: string | null) => {
  return formatWorkerDateLabel(value);
};

const locationFor = (task: Task) =>
  task.workLocation ?? task.workLocationRef?.name ?? task.component?.unit?.code ?? task.component?.name ?? 'Site';

const progressFor = (task?: Task | null) => {
  if (!task) return 0;
  const status = normalizeStatus(task.workerLifecycleStatus ?? task.lifecycleStatus ?? task.status);
  if (task.workerLifecycleStatus) {
    if (status === 'IN_PROGRESS') return 1;
    if (status === 'INSPECTION_PENDING') return 35;
    if (status === 'INSPECTION_PASSED') return 100;
    return 0;
  }
  if (Number.isFinite(Number(task.percentComplete))) return Math.max(0, Math.min(100, Number(task.percentComplete)));
  const estimated = Number(task.estimatedHours ?? 0);
  const actual = Number(task.actualHours ?? 0);
  if (estimated > 0 && actual > 0) return Math.max(5, Math.min(100, Math.round((actual / estimated) * 100)));
  if (status === 'IN_PROGRESS') return 1;
  if (status === 'INSPECTION_PENDING') return 35;
  if (status === 'INSPECTION_PASSED') return 100;
  return 0;
};

const plannedHoursFor = (task?: Task | null) => Number(task?.estimatedHours ?? 0);
const actualHoursFor = (task?: Task | null) => {
  const workerSeconds = Number(task?.workerActualSeconds ?? 0);
  if (task?.workerLifecycleStatus) return workerSeconds / 3600;
  return workerSeconds > 0 ? workerSeconds / 3600 : Number(task?.actualHours ?? 0);
};

const reviewerNameFor = (task?: Task | null) =>
  task?.reviewedByName ?? task?.reviewedBy?.name ?? task?.reviewedBy?.email ?? null;

const reviewDateLabelFor = (task?: Task | null) => task?.reviewedAt ? formatDate(task.reviewedAt) : null;

const formatActualDuration = (hours: number, fallbackSeconds = 0) => {
  if (!Number.isFinite(hours) || hours <= 0) return formatElapsed(fallbackSeconds);
  const totalMinutes = Math.max(1, Math.round(hours * 60));
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const wholeHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes ? `${wholeHours}h ${minutes}m` : `${wholeHours}h`;
};

const formatElapsed = (totalSeconds: number) => {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    : `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const elapsedSecondsFor = (task?: Task | null, now = Date.now()) => {
  if (!task) return 0;
  const actualHours = actualHoursFor(task);
  const actualSeconds = Math.max(0, Math.round(actualHours * 3600));
  const status = normalizeStatus(task.workerLifecycleStatus ?? task.lifecycleStatus ?? task.status);
  const completedAt = (task.workerCompletedAt ?? task.completionDate)
    ? new Date(task.workerCompletedAt ?? task.completionDate as string).getTime()
    : null;
  const startedAt = (task.workerActualStartDate ?? task.actualStartDate)
    ? new Date(task.workerActualStartDate ?? task.actualStartDate as string).getTime()
    : null;
  if (startedAt && Number.isFinite(startedAt) && status === 'IN_PROGRESS') {
    return actualSeconds + Math.max(0, Math.floor((now - startedAt) / 1000));
  }
  if (actualSeconds > 0) {
    return actualSeconds;
  }
  if (startedAt && Number.isFinite(startedAt) && completedAt && Number.isFinite(completedAt)) {
    return Math.max(0, Math.floor((completedAt - startedAt) / 1000));
  }
  return 0;
};

const sortByDate = (a: Task, b: Task) =>
  String(a.dueDate ?? a.startDate ?? (a as any).plannedStartDate ?? '').localeCompare(String(b.dueDate ?? b.startDate ?? (b as any).plannedStartDate ?? ''));

const isNetworkFailure = (err: any) => !err?.response || err?.code === 'ERR_NETWORK';

const resourceLabel = (resource: any) => {
  if (!resource) return 'Resource';
  const name = resource.name ?? resource.resourceName ?? resource.resource?.name ?? resource.description ?? 'Resource';
  const qty = resource.quantity ?? resource.qty ?? resource.requiredQuantity ?? resource.quantityRequired ?? resource.estimatedQty;
  const unit = resource.unit ?? resource.uom ?? resource.resource?.unit ?? resource.resource?.unitOfMeasure ?? '';
  return qty ? `${name} - ${qty}${unit ? ` ${unit}` : ''}` : name;
};

const formatMaterialQty = (value?: number | string | null) => {
  const numberValue = Number(value ?? 0);
  return Number.isFinite(numberValue) ? numberValue.toLocaleString() : String(value ?? 0);
};

const taskMaterialLabel = (material: TaskMaterial) => {
  const name = material.resource?.name ?? material.resourceId;
  const code = material.resource?.resourceCode ? ` (${material.resource.resourceCode})` : '';
  const unit = material.resource?.unitOfMeasure ? ` ${material.resource.unitOfMeasure}` : '';
  return `${name}${code} - ${formatMaterialQty(material.quantityRequired)}${unit}`;
};

const upcomingTasksFrom = (tasks: Task[], currentTaskId?: string) =>
  [...tasks]
    .filter((task) => task.id !== currentTaskId)
    .filter((task) => !['INSPECTION_PASSED', 'CANCELLED'].includes(normalizeStatus(task.workerLifecycleStatus ?? task.lifecycleStatus ?? task.status)))
    .sort(sortByDate);

const ACTIVE_DEFECT_STATUSES = new Set(['open', 'assigned', 'fixing', 'recheck_pending']);

const defectsForTask = (defects: DefectBoardRow[], task?: Task | null) => {
  if (!task) return [];
  return defects
    .filter((defect) => defect.originalTaskId === task.id || defect.remedialTaskId === task.id)
    .filter((defect) => ACTIVE_DEFECT_STATUSES.has(String(defect.status).toLowerCase()))
    .sort((a, b) => String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? '')));
};

const todayInput = () => new Date().toISOString().slice(0, 10);

const monthsAgoInput = (months: number) => {
  const date = new Date();
  date.setMonth(date.getMonth() - months);
  return date.toISOString().slice(0, 10);
};

const fourDigitScanNumber = (source?: string | null) => {
  const digits = String(source ?? '').replace(/\D/g, '');
  if (digits) return digits.slice(-4).padStart(4, '0');
  let hash = 0;
  String(source ?? 'field-worker').split('').forEach((char) => {
    hash = (hash * 31 + char.charCodeAt(0)) % 10000;
  });
  return String(hash).padStart(4, '0');
};

function WorkerHeaderScanCode({ code }: { code: string }) {
  const encoded = useMemo(() => {
    const normalized = code.toUpperCase().split('').filter((char) => Boolean(CODE39_PATTERNS[char])).join('');
    if (!normalized) return null;
    const modules: { x: number; width: number }[] = [];
    let x = 0;
    `*${normalized}*`.split('').forEach((char) => {
      const pattern = CODE39_PATTERNS[char];
      pattern.split('').forEach((widthKey, index) => {
        const width = widthKey === 'w' ? 3.2 : 1.35;
        if (index % 2 === 0) modules.push({ x, width });
        x += width;
      });
      x += 1.6;
    });
    return { normalized, modules, width: x };
  }, [code]);

  if (!encoded) return null;

  return (
    <Box
      sx={{
        width: { xs: 138, sm: 172, md: 196 },
        flexShrink: 0,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: '#fff',
        px: { xs: 0.8, sm: 1 },
        py: { xs: 0.65, sm: 0.8 },
      }}
    >
      <svg
        viewBox={`0 0 ${encoded.width} 44`}
        role="img"
        aria-label={`Worker scan code ${encoded.normalized}`}
        style={{ width: '100%', height: 40, display: 'block' }}
        preserveAspectRatio="none"
      >
        <rect x="0" y="0" width={encoded.width} height="44" fill="#fff" />
        {encoded.modules.map((module, index) => (
          <rect key={`${module.x}-${index}`} x={module.x} y="0" width={module.width} height="44" fill="#111" />
        ))}
      </svg>
      <Typography sx={{ fontSize: { xs: 13, sm: 15 }, fontWeight: 900, lineHeight: 1, textAlign: 'center', color: '#111', mt: 0.45 }}>
        {encoded.normalized}
      </Typography>
    </Box>
  );
}

function PageTitle({ data, title }: { data: ReturnType<typeof useWorkerFieldData>; title: string }) {
  const workerNames = data.worker?.name?.split(/\s+/) ?? [];
  const initials = data.worker
    ? `${workerNames[0]?.[0] ?? ''}${workerNames[1]?.[0] ?? ''}` || 'W'
    : `${data.user?.firstName?.[0] ?? ''}${data.user?.lastName?.[0] ?? ''}` || 'W';
  const scanCode = `FE${fourDigitScanNumber(data.worker?.badgeCode ?? data.worker?.id ?? data.user?.id)}`;
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>{title}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.1 }}>{data.team?.name ?? 'Construction Team'}</Typography>
        <Typography variant="body2" color="text.secondary">{data.worker?.name ? `${data.worker.name} - ${data.projectName}` : data.projectName}</Typography>
      </Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
        <WorkerHeaderScanCode code={scanCode} />
        <Box sx={{ width: { xs: 48, sm: 54 }, height: { xs: 48, sm: 54 }, borderRadius: '50%', bgcolor: 'primary.main', color: 'primary.contrastText', display: 'grid', placeItems: 'center', fontWeight: 900 }}>
          {initials}
        </Box>
      </Stack>
    </Stack>
  );
}

function MetricCard({ label, value, detail, breakdown = [] }: { label: string; value: string | number; detail: string; breakdown?: string[] }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, bgcolor: '#fff', minHeight: 102 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>{label}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.1 }}>{value}</Typography>
      <Typography variant="body2" color="text.secondary">{detail}</Typography>
      {breakdown.length > 0 && (
        <Stack spacing={0.5} sx={{ mt: 1.25 }}>
          {breakdown.map((item) => (
            <Typography key={item} variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>
              {item}
            </Typography>
          ))}
        </Stack>
      )}
    </Paper>
  );
}

function MenuBar({ to, label, detail }: { to: string; label: string; detail: string }) {
  return (
    <Paper
      component={RouterLink}
      to={to}
      variant="outlined"
      sx={{ p: 2, borderRadius: 1, bgcolor: '#fff', color: 'inherit', textDecoration: 'none', display: 'block' }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 900 }}>{label}</Typography>
          <Typography variant="body2" color="text.secondary">{detail}</Typography>
        </Box>
        <Typography sx={{ fontWeight: 900, color: 'text.secondary' }}>Open</Typography>
      </Stack>
    </Paper>
  );
}

const workerHomePath = (navigationBasePath: string, screen: 'current' | 'upcoming' | 'performance' | 'emergency' | 'attendance') => {
  if (navigationBasePath !== '/field') return `${navigationBasePath}/${screen}`;
  if (screen === 'current') return '/field/tasks';
  if (screen === 'upcoming') return '/field/upcoming';
  if (screen === 'performance') return '/field/performance';
  if (screen === 'attendance') return '/field/attendance';
  return '/field/worker/emergency';
};

function HomeView({ data, navigationBasePath }: { data: ReturnType<typeof useWorkerFieldData>; navigationBasePath: string }) {
  const currentTask = selectCurrentWorkerTask(data.tasks);
  const upcomingTasks = upcomingTasksFrom(data.tasks, currentTask?.id);
  const summary = useMemo(() => computeWorkerPerformance(data.tasks, data.defects, data.attendance, 'month'), [data.attendance, data.defects, data.tasks]);
  const pendingApprovals = data.tasks.filter((task) => normalizeStatus(task.workerLifecycleStatus ?? task.lifecycleStatus ?? task.status) === 'INSPECTION_PENDING').length;
  const attendanceToday = todayInput();
  const [attendancePromptHiddenForDate, setAttendancePromptHiddenForDate] = useState<string | null>(null);
  const [attendanceBusy, setAttendanceBusy] = useState(false);
  const [attendanceError, setAttendanceError] = useState<string | null>(null);
  const todayAttendance = useMemo(
    () => data.attendance.find((row) => row.attendanceDate === attendanceToday) ?? null,
    [attendanceToday, data.attendance]
  );
  const showAttendancePrompt = navigationBasePath === '/field' && attendancePromptHiddenForDate !== attendanceToday && !todayAttendance?.scanInAt;

  const logInToday = async () => {
    if (!data.projectId || !data.worker) return;
    try {
      setAttendanceBusy(true);
      setAttendanceError(null);
      const now = new Date().toISOString();
      const payload = {
        projectId: data.projectId,
        workerId: data.worker.id,
        attendanceDate: attendanceToday,
        method: 'manual' as const,
        scanInAt: now,
      };
      if (navigator.onLine) {
        await attendanceService.record(payload);
      } else {
        await enqueueOfflineAttendance(payload, data.worker);
      }
      setAttendancePromptHiddenForDate(attendanceToday);
      data.reload();
    } catch (err: any) {
      setAttendanceError(err.response?.data?.error || err.response?.data?.message || 'Attendance log-in failed.');
    } finally {
      setAttendanceBusy(false);
    }
  };

  return (
    <Stack spacing={2}>
      {showAttendancePrompt && (
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: 1,
            bgcolor: '#fff7e6',
            borderColor: 'warning.main',
            borderLeft: 5,
            borderLeftColor: 'warning.main',
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'center' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 900 }}>Remember to log in</Typography>
              <Typography variant="body2" color="text.secondary">
                Start today&apos;s attendance before work begins.
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="warning"
              startIcon={<Login />}
              disabled={!data.worker || attendanceBusy}
              onClick={logInToday}
              sx={{ color: 'warning.contrastText', fontWeight: 900, minWidth: 120 }}
            >
              Log In
            </Button>
          </Stack>
          {attendanceError && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setAttendanceError(null)}>{attendanceError}</Alert>}
        </Paper>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 1 }}>
        <MetricCard label="Quality" value={`${summary.qualityScore}%`} detail={`${summary.passed} passed, ${summary.failed} failed`} />
        <MetricCard label="Speed" value={`${summary.speedScore}%`} detail={`${summary.aheadOrOnTime} on plan`} />
        <MetricCard label="Attendance" value={`${summary.attendanceScore}%`} detail={`${summary.presentDays} present, ${summary.lateDays} late`} />
        <MetricCard label="Pending approval" value={pendingApprovals} detail="Completed tasks awaiting review" />
      </Box>

      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, bgcolor: '#fff' }}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>Current task status</Typography>
              <Typography sx={{ fontWeight: 900 }}>{currentTask?.title ?? 'No active task assigned'}</Typography>
            </Box>
            <Chip size="small" label={`${progressFor(currentTask)}%`} color="primary" />
          </Stack>
          <LinearProgress variant="determinate" value={progressFor(currentTask)} sx={{ height: 8, borderRadius: 1 }} />
        </Stack>
      </Paper>

      <MenuBar to={workerHomePath(navigationBasePath, 'current')} label="Current task" detail={currentTask ? `${currentTask.taskCode ?? 'Task'} - ${locationFor(currentTask)}` : 'Start, pause, reset, complete, and review task details'} />
      {navigationBasePath === '/field' && (
        <MenuBar to={workerHomePath(navigationBasePath, 'attendance')} label="Attendance" detail="Log in and log out for today" />
      )}
      <MenuBar to={workerHomePath(navigationBasePath, 'upcoming')} label="Upcoming tasks / work plan" detail={`${upcomingTasks.length} assigned upcoming tasks`} />
      <MenuBar to={workerHomePath(navigationBasePath, 'performance')} label="Performance history" detail="Quality, speed, attendance, bonus, and completed work records" />

      <Button component={RouterLink} to={workerHomePath(navigationBasePath, 'emergency')} variant="contained" size="large" sx={{ py: 1.4 }}>
        Safety & Emergency
      </Button>
    </Stack>
  );
}

function CurrentTaskView({
  tasks,
  taskResources,
  defects,
  reload,
  onReturnToFieldMode,
}: {
  tasks: Task[];
  taskResources: Record<string, TaskMaterial[]>;
  defects: DefectBoardRow[];
  reload: () => void;
  onReturnToFieldMode: () => void;
}) {
  const [expanded, setExpanded] = useState<string | false>('location');
  const [taskOverride, setTaskOverride] = useState<Task | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [completionOpen, setCompletionOpen] = useState(false);
  const [completionDurationSeconds, setCompletionDurationSeconds] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  const baseTask = selectCurrentWorkerTask(tasks);
  const task = baseTask
    ? taskOverride && taskOverride.id === baseTask.id
      ? taskOverride
      : baseTask
    : completionOpen && taskOverride
      ? taskOverride
      : null;

  const displayTask = task;
  const status = displayTask ? normalizeStatus(displayTask.workerLifecycleStatus ?? displayTask.lifecycleStatus ?? displayTask.status) : '';

  useEffect(() => {
    if (status !== 'IN_PROGRESS') return;
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [status, displayTask?.id]);

  const updateLifecycle = async (
    nextStatus: 'IN_PROGRESS' | 'PAUSED' | 'INSPECTION_PENDING',
    options: { showCompletionOverlay?: boolean } = {}
  ) => {
    if (!displayTask) return;
    setBusy(true);
    setActionError(null);
    try {
      const transitionNow = Date.now();
      const transitionNowIso = new Date(transitionNow).toISOString();
      const elapsedBeforeTransition = elapsedSecondsFor(displayTask, transitionNow);
      const rawUpdated = navigator.onLine
        ? await tasksService.updateDispatchedWorkerTaskStatus(displayTask.id, nextStatus)
        : await enqueueOfflineTaskPatch(displayTask, { lifecycleStatus: nextStatus.toLowerCase() }, { lifecycleStatus: nextStatus, status: nextStatus });
      const updated: Task = {
        ...rawUpdated,
        workerLifecycleStatus: nextStatus,
        workerActualStartDate: nextStatus === 'IN_PROGRESS' ? rawUpdated.workerActualStartDate ?? transitionNowIso : rawUpdated.workerActualStartDate,
        workerCompletedAt: nextStatus === 'INSPECTION_PENDING' ? rawUpdated.workerCompletedAt ?? transitionNowIso : rawUpdated.workerCompletedAt ?? null,
        actualHours: nextStatus === 'PAUSED' || nextStatus === 'INSPECTION_PENDING'
          ? Number(rawUpdated.actualHours ?? 0) || Math.max(elapsedBeforeTransition, 1) / 3600
          : Number(rawUpdated.actualHours ?? displayTask.actualHours ?? 0),
      };
      setTaskOverride(updated);
      if (options.showCompletionOverlay) {
        setCompletionDurationSeconds(elapsedSecondsFor(updated, transitionNow) || Math.max(elapsedBeforeTransition, 1));
        setCompletionOpen(true);
      }
      if (navigator.onLine) {
        setNotice(nextStatus === 'IN_PROGRESS' ? 'Task started.' : nextStatus === 'PAUSED' ? 'Task paused.' : 'Task submitted for inspection.');
        reload();
      } else {
        setNotice('Task status queued for sync.');
      }
    } catch (err) {
      if (isNetworkFailure(err)) {
        const queued = await enqueueOfflineTaskPatch(displayTask, { lifecycleStatus: nextStatus.toLowerCase() }, { lifecycleStatus: nextStatus, status: nextStatus });
        setTaskOverride(queued);
        setNotice('Task status queued for sync.');
      } else {
        setActionError((err as any)?.response?.data?.error || (err as any)?.response?.data?.message || 'Task status could not be changed.');
      }
    } finally {
      setBusy(false);
    }
  };

  const undoCompletion = async () => {
    await updateLifecycle('IN_PROGRESS');
    setCompletionOpen(false);
    setCompletionDurationSeconds(0);
  };

  if (!displayTask) {
    return (
      <Stack spacing={2}>
        {actionError && <Alert severity="error" onClose={() => setActionError(null)}>{actionError}</Alert>}
        <Alert severity="info">No current task is assigned to this worker account.</Alert>
      </Stack>
    );
  }

  const safety = taskSafetyProfile(displayTask);
  const linkedMaterials = taskResources[displayTask.id] ?? [];
  const pickingItems = linkedMaterials.length
    ? linkedMaterials.map(taskMaterialLabel)
    : Array.isArray(displayTask.resourcesRequired)
      ? displayTask.resourcesRequired.map(resourceLabel)
      : [];
  const workerSpecs = workerSpecificationItems(displayTask);
  const canStart = canStartTask(displayTask);
  const canPause = status === 'IN_PROGRESS';
  const canComplete = ['IN_PROGRESS', 'PAUSED'].includes(status);
  const canUndoComplete = status === 'INSPECTION_PENDING';
  const isRework = status === 'INSPECTION_FAILED';
  const elapsedSeconds = elapsedSecondsFor(displayTask, now);
  const reworkDefects = defectsForTask(defects, task);
  const reworkFallbackNote = (displayTask as any).rejectionReason || (displayTask as any).rejection_reason || displayTask.notes || '';
  const primaryReworkInstruction = reworkDefects.find((defect) => defect.description)?.description || reworkFallbackNote;
  const showReworkInstructions = isRework || reworkDefects.length > 0 || Boolean(reworkFallbackNote);
  const reviewerName = reviewerNameFor(displayTask);
  const reviewDateLabel = reviewDateLabelFor(displayTask);

  return (
    <Stack spacing={2}>
      <WorkerTaskCompleteOverlay
        open={completionOpen}
        busy={busy}
        durationLabel={formatElapsed(completionDurationSeconds)}
        onReturn={() => {
          setCompletionOpen(false);
          onReturnToFieldMode();
        }}
        onUndo={undoCompletion}
      />
      {actionError && <Alert severity="error" onClose={() => setActionError(null)}>{actionError}</Alert>}
      {isRework && (
        <Alert severity="warning">
          This task failed inspection{reviewerName ? ` by ${reviewerName}` : ''}{reviewDateLabel ? ` on ${reviewDateLabel}` : ''} and has been returned for rework. Start rework, correct the issue, then complete it again for reinspection.
        </Alert>
      )}
      {status === 'INSPECTION_PASSED' && reviewerName && (
        <Alert severity="success">
          This task was approved by {reviewerName}{reviewDateLabel ? ` on ${reviewDateLabel}` : ''}.
        </Alert>
      )}
      {canUndoComplete && (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" disabled={busy} onClick={() => updateLifecycle('IN_PROGRESS')}>
              Undo complete
            </Button>
          }
        >
          This task was submitted for inspection. If that was premature, undo it and continue working.
        </Alert>
      )}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, bgcolor: '#fff' }}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>Task name</Typography>
            <Typography variant="h5" sx={{ fontWeight: 900 }}>{displayTask.title}</Typography>
            <Typography variant="body2" color="text.secondary">{displayTask.taskCode ?? 'Task'} - {locationFor(displayTask)}</Typography>
          </Box>
        </Stack>

        {showReworkInstructions && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, bgcolor: '#fff7ed', borderColor: 'warning.light', mt: 2 }}>
            <Stack spacing={1.25}>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>Rework instructions</Typography>
                <Typography sx={{ fontWeight: 900 }}>
                  {primaryReworkInstruction || 'Confirm the correction details with the supervisor before starting rework.'}
                </Typography>
              </Box>
              {reworkDefects.length > 0 && (
                <Stack spacing={1}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>Other rework details</Typography>
                  {reworkDefects.map((defect) => (
                    <Paper key={defect.id} variant="outlined" sx={{ p: 1.5, borderRadius: 1, bgcolor: '#fff' }}>
                      <Stack spacing={0.75}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 900 }}>{defect.title}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(defect.createdAt)}{defect.raisedByName ? ` - Raised by ${defect.raisedByName}` : ''}
                            </Typography>
                          </Box>
                          <Chip size="small" color={defect.severity === 'critical' || defect.severity === 'high' ? 'error' : 'warning'} label={defect.severity} />
                        </Stack>
                        <Typography color="text.secondary">{defect.description || 'No detailed notes were entered.'}</Typography>
                        {defect.assignedToTeamName && (
                          <Typography variant="caption" color="text.secondary">Assigned to {defect.assignedToTeamName}</Typography>
                        )}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Stack>
          </Paper>
        )}

        <Box sx={{ mt: 2 }}>
          <TaskPhotoEvidencePanel
            projectId={displayTask.projectId}
            taskIds={[displayTask.id]}
            title="Photos for this task"
            dense
          />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1, mt: 2 }}>
          <Button
            variant="contained"
            disabled={busy || !canStart}
            onClick={() => updateLifecycle('IN_PROGRESS')}
          >
            {isRework ? 'Start Rework' : 'Start'}
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={busy || !canPause}
            onClick={() => updateLifecycle('PAUSED')}
          >
            Pause
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={busy || !canComplete}
            onClick={() => updateLifecycle('INSPECTION_PENDING', { showCompletionOverlay: true })}
          >
            Complete
          </Button>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>Time taken</Typography>
            <Typography sx={{ fontWeight: 900 }}>
              {formatElapsed(elapsedSeconds)} / {plannedHoursFor(displayTask).toFixed(1)} hrs planned
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Timer is based on the task start timestamp, so elapsed time catches up when the phone comes back on.
            </Typography>
          </Box>
          <Chip
            color={status === 'IN_PROGRESS' ? 'success' : isRework ? 'warning' : 'default'}
            label={status === 'IN_PROGRESS' ? 'Timer running' : isRework ? 'Needs rework' : status.replace(/_/g, ' ')}
          />
        </Stack>
        <LinearProgress variant="determinate" value={progressFor(displayTask)} sx={{ height: 8, borderRadius: 1, mt: 1 }} />
      </Paper>

      {[
        ['location', 'Location', `${locationFor(displayTask)}. ${displayTask.description ?? 'Confirm exact work face with supervisor before starting.'}`],
        ['picking', 'Picking list', pickingItems.length ? pickingItems : 'No picking list for this task.'],
        ['specifications', 'Specifications', workerSpecs.length ? workerSpecs : safety.protection],
        ['drawings', 'Drawing details', (displayTask as any).drawingDetails ?? (displayTask as any).drawingReference ?? 'Latest issued drawing and detail references are required before completion.'],
      ].map(([id, label, content]) => (
        <Accordion key={String(id)} expanded={expanded === id} onChange={(_, open) => setExpanded(open ? String(id) : false)} disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 900 }}>{String(label)}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {id === 'location' ? (
              <Stack spacing={1.25}>
                <Typography color="text.secondary">{String(content)}</Typography>
                <SitePlanMap task={displayTask} compact />
              </Stack>
            ) : Array.isArray(content) && id === 'picking' ? (
              <Stack spacing={1}>
                {content.map((item, index) => (
                  <Stack key={`${String(id)}-${index}`} direction="row" spacing={1} alignItems="flex-start">
                    <Box sx={{ width: 16, height: 16, mt: 0.25, border: '2px solid', borderColor: 'text.secondary', borderRadius: 0.5, flex: '0 0 auto' }} />
                    <Typography>{String(item)}</Typography>
                  </Stack>
                ))}
              </Stack>
            ) : Array.isArray(content) ? (
              <Stack spacing={1}>
                {content.map((item, index) => (
                  <Stack key={`${String(id)}-${index}`} direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 18, height: 18, border: '2px solid', borderColor: 'text.secondary', borderRadius: 0.5 }} />
                    <Typography>{typeof item === 'string' ? item : item?.text ?? item?.name ?? 'Requirement'}</Typography>
                  </Stack>
                ))}
              </Stack>
            ) : (
              <Typography color="text.secondary">{String(content)}</Typography>
            )}
          </AccordionDetails>
        </Accordion>
      ))}

      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={4000}
        onClose={() => setNotice(null)}
        message={notice ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Stack>
  );
}

function UpcomingView({ tasks }: { tasks: Task[] }) {
  const [range, setRange] = useState<'week' | 'month'>('week');
  const currentTask = selectCurrentWorkerTask(tasks);
  const upcomingTasks = upcomingTasksFrom(tasks, currentTask?.id);
  const displayTasks = upcomingTasks.length
    ? upcomingTasks.slice(0, DISPLAY_ROW_LIMIT)
    : [];
  const days = range === 'week' ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] : Array.from({ length: 30 }, (_, index) => String(index + 1));

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="h6" sx={{ fontWeight: 900, flex: 1 }}>Upcoming tasks / work plan</Typography>
        <ToggleButtonGroup exclusive size="small" value={range} onChange={(_, value) => value && setRange(value)}>
          <ToggleButton value="week">Week</ToggleButton>
          <ToggleButton value="month">Month</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Paper variant="outlined" sx={{ p: 1, borderRadius: 1, bgcolor: '#fff' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: range === 'week' ? 'repeat(7, 1fr)' : 'repeat(6, 1fr)', gap: 0.75 }}>
          {days.map((day, index) => (
            <Box key={day} sx={{ minHeight: range === 'week' ? 62 : 42, border: '1px solid', borderColor: 'divider', borderRadius: 0.75, p: 0.75 }}>
              <Typography variant="caption" sx={{ fontWeight: 900 }}>{day}</Typography>
              {index < displayTasks.length && <Box sx={{ mt: 0.5, height: 6, bgcolor: 'primary.main', borderRadius: 1 }} />}
            </Box>
          ))}
        </Box>
      </Paper>

      <Typography variant="h6" sx={{ fontWeight: 900 }}>Task schedule</Typography>
      <Stack spacing={1.25}>
        {!upcomingTasks.length && <Alert severity="info">No upcoming tasks are assigned to this worker account.</Alert>}
        {upcomingTasks.length > DISPLAY_ROW_LIMIT && (
          <Typography variant="caption" color="text.secondary">Showing first {DISPLAY_ROW_LIMIT} of {upcomingTasks.length} assigned tasks for review.</Typography>
        )}
        {displayTasks.map((task) => (
          <Paper key={task.id} variant="outlined" sx={{ p: 2, borderRadius: 1, bgcolor: '#fff' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 900 }}>{task.title}</Typography>
                <Typography variant="body2" color="text.secondary">{task.taskCode ?? 'Task'} - {locationFor(task)} - Due {formatDate(task.dueDate)}</Typography>
              </Box>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Stack>
  );
}

function PerformanceView({ tasks, defects, attendance }: { tasks: Task[]; defects: DefectBoardRow[]; attendance: AttendanceRecord[] }) {
  const [period, setPeriod] = useState<WorkerPeriod>('month');
  const [customFrom, setCustomFrom] = useState(monthsAgoInput(1));
  const [customTo, setCustomTo] = useState(todayInput());
  const customRange = useMemo(() => ({ from: customFrom, to: customTo }), [customFrom, customTo]);
  const summary = useMemo(() => computeWorkerPerformance(tasks, defects, attendance, period, new Date(), customRange), [attendance, customRange, defects, period, tasks]);
  const completedTasks = tasks
    .filter((task) => ['INSPECTION_PENDING', 'INSPECTION_PASSED', 'INSPECTION_FAILED'].includes(normalizeStatus(task.workerLifecycleStatus ?? task.lifecycleStatus ?? task.status)))
    .filter((task) => dateInWorkerPeriod(taskResultDate(task), period, new Date(), customRange))
    .sort((a, b) => String(taskResultDate(b)).localeCompare(String(taskResultDate(a))));
  const displayCompletedTasks = completedTasks.length
    ? completedTasks
    : [];
  const completedTaskGroups = useMemo(() => {
    const groups = new Map<string, Task[]>();
    displayCompletedTasks.forEach((task) => {
      const key = taskResultDateKey(task) || 'No completion date';
      groups.set(key, [...(groups.get(key) ?? []), task]);
    });
    return [...groups.entries()];
  }, [displayCompletedTasks]);
  const bonusScore = Math.round((summary.qualityScore * 0.4) + (summary.speedScore * 0.35) + (summary.attendanceScore * 0.25));

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} justifyContent="flex-end">
        <ToggleButtonGroup exclusive value={period} onChange={(_, value) => value && setPeriod(value)} size="small" sx={{ bgcolor: '#fff' }}>
          <ToggleButton value="month">Last month</ToggleButton>
          <ToggleButton value="quarter">Last 3 months</ToggleButton>
          <ToggleButton value="custom">Custom</ToggleButton>
          <ToggleButton value="all">All time</ToggleButton>
        </ToggleButtonGroup>
        {period === 'custom' && (
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              type="date"
              label="From"
              InputLabelProps={{ shrink: true }}
              value={customFrom}
              onChange={(event) => setCustomFrom(event.target.value)}
            />
            <TextField
              size="small"
              type="date"
              label="To"
              InputLabelProps={{ shrink: true }}
              value={customTo}
              onChange={(event) => setCustomTo(event.target.value)}
            />
          </Stack>
        )}
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 1 }}>
        <MetricCard
          label="Quality"
          value={`${summary.qualityScore}%`}
          detail={`${summary.passed} passed, ${summary.failed} failed`}
          breakdown={[
            `${summary.defectCount} defects recorded in period`,
            'Failed inspections and defects reduce this score',
          ]}
        />
        <MetricCard
          label="Speed"
          value={`${summary.speedScore}%`}
          detail={`${summary.aheadOrOnTime} on plan, ${summary.lateTasks} late`}
          breakdown={[
            'Compares actual hours against planned duration',
            'Tasks completed within estimate count as on plan',
          ]}
        />
        <MetricCard
          label="Attendance"
          value={`${summary.attendanceScore}%`}
          detail={`${summary.hours} recorded hours`}
          breakdown={[
            `${summary.presentDays} present, ${summary.lateDays} late, ${summary.absentDays} absent`,
            'Late days receive partial attendance credit',
          ]}
        />
        <MetricCard
          label="Bonus score"
          value={bonusScore}
          detail="Accumulated performance score"
          breakdown={[
            'Quality 40%, speed 35%, attendance 25%',
            'Used as the overall worker bonus indicator',
          ]}
        />
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
        <Box sx={{ p: 1.5, borderBottom: completedTaskGroups.length ? '1px solid' : 0, borderColor: 'divider' }}>
          <Typography fontWeight={900}>Completed Task History</Typography>
          <Typography variant="caption" color="text.secondary">
            {completedTasks.length ? `${completedTasks.length} completed task${completedTasks.length === 1 ? '' : 's'} in this period` : 'Grouped by completion date'}
          </Typography>
        </Box>
        {completedTaskGroups.map(([dateKey, group]) => (
          <Accordion
            key={dateKey}
            disableGutters
            variant="outlined"
            sx={{ borderX: 0, borderTop: 0, borderRadius: 0, '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'grey.50', minHeight: 48, '& .MuiAccordionSummary-content': { my: 0.75 } }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: '100%', pr: 1 }}
              >
                <Typography fontWeight={900}>{dateKey === 'No completion date' ? dateKey : formatDate(dateKey)}</Typography>
                <Chip size="small" label={`${group.length} task${group.length === 1 ? '' : 's'}`} />
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {group.map((task) => {
                const status = normalizeStatus(task.workerLifecycleStatus ?? task.lifecycleStatus ?? task.status);
                const passed = status === 'INSPECTION_PASSED';
                const pending = status === 'INSPECTION_PENDING';
                const score = passed ? 100 : pending ? 75 : 60;
                const planned = plannedHoursFor(task);
                const actual = actualHoursFor(task);
                const reviewerName = reviewerNameFor(task);
                const reviewDateLabel = reviewDateLabelFor(task);
                const reviewSummary = pending
                  ? `Submitted for inspection${taskResultDate(task) ? ` on ${formatDate(taskResultDate(task))}` : ''}`
                  : `${passed ? 'Approved' : 'Failed'} by ${reviewerName ?? 'Supervisor not recorded'}${reviewDateLabel ? ` on ${reviewDateLabel}` : ''}`;
                return (
                  <Accordion key={task.id} disableGutters variant="outlined" sx={{ borderX: 0, borderBottom: 0, '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { my: 0.75 } }}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={0.75}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        justifyContent="space-between"
                        sx={{ width: '100%', pr: 1 }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={900} noWrap>{task.title ?? task.taskCode ?? 'Completed task'}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {task.taskCode ?? 'Task'} - {locationFor(task)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 800 }} noWrap>
                            {reviewSummary}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Chip size="small" color={passed ? 'success' : pending ? 'info' : 'warning'} label={passed ? 'Inspection passed' : pending ? 'Submitted' : 'Inspection failed'} />
                          <Typography variant="body2" fontWeight={900}>{score}</Typography>
                        </Stack>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' }, gap: 1 }}>
                        <MetricCard label="Score" value={score} detail={passed ? 'passed inspection' : pending ? 'awaiting supervisor inspection' : 'needs rework'} />
                        <MetricCard label="Planned" value={`${planned}h`} detail="estimated duration" />
                        <MetricCard label="Actual" value={formatActualDuration(actual, elapsedSecondsFor(task))} detail="recorded time" />
                        <MetricCard
                          label={passed ? 'Approved by' : 'Failed by'}
                          value={reviewerName ?? 'Supervisor not recorded'}
                          detail={reviewDateLabel ?? locationFor(task)}
                        />
                      </Box>
                      <Box sx={{ mt: 1.5 }}>
                        <TaskPhotoEvidencePanel
                          projectId={task.projectId}
                          taskIds={[task.id]}
                          title="Photos for this task"
                          dense
                        />
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </AccordionDetails>
          </Accordion>
        ))}
        {completedTasks.length === 0 && <Box sx={{ p: 2 }}><Typography color="text.secondary">No completed task history in this period.</Typography></Box>}
      </Paper>
    </Stack>
  );
}

function EmergencyView({ tasks, contacts, incidents, toolboxTalks }: { tasks: Task[]; contacts: any[]; incidents: any[]; toolboxTalks: any[] }) {
  const [expanded, setExpanded] = useState<string | false>('toolbox');
  const ppe = Array.from(new Set(tasks.flatMap((task) => taskSafetyProfile(task).protection)));
  const contactLabel = (contact: any) => {
    const name = contact.name ?? contact.contactName ?? 'Emergency contact';
    const role = contact.contactRole ?? contact.role;
    const phone = contact.phone ?? contact.phoneNumber ?? contact.mobile ?? '-';
    return [name, role, phone].filter(Boolean).join(' - ');
  };
  const contactPhone = (contact: any) => String(contact.phone ?? contact.phoneNumber ?? contact.mobile ?? '').trim();
  const contactHref = (contact: any) => {
    const phone = contactPhone(contact).replace(/[^\d+]/g, '');
    return phone ? `tel:${phone}` : undefined;
  };

  const sections = [
    ['toolbox', 'Toolbox talks - information', toolboxTalks.length ? toolboxTalks.map((talk) => talk.topic ?? talk.title ?? talk.subject ?? 'Toolbox talk').slice(0, DISPLAY_ROW_LIMIT) : []],
    ['gear', 'Safety gears assigned', ppe],
    ['contacts', 'Emergency contact information', contacts.length ? contacts.map(contactLabel) : []],
  ];

  return (
    <Stack spacing={2}>
      {sections.map(([id, label, rows]) => (
        <Accordion key={String(id)} expanded={expanded === id} onChange={(_, open) => setExpanded(open ? String(id) : false)} disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 900 }}>{String(label)}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {String(id) === 'contacts' && contacts.length ? contacts.map((contact, index) => (
                <Button
                  key={`contact-${contact.id ?? index}`}
                  component="a"
                  href={contactHref(contact)}
                  variant="outlined"
                  color="error"
                  disabled={!contactPhone(contact)}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1.25, borderRadius: 1 }}
                >
                  {contactLabel(contact)}
                </Button>
              )) : (rows as string[]).length ? (rows as string[]).map((row, index) => (
                <Paper key={`${String(id)}-${index}`} variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
                  <Typography>{row}</Typography>
                </Paper>
              )) : <Typography color="text.secondary">No records loaded for this section.</Typography>}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}

      {incidents.length > 0 && (
        <>
          <Divider />
          <Typography sx={{ fontWeight: 900 }}>Recent safety records</Typography>
          {incidents.slice(0, DISPLAY_ROW_LIMIT).map((incident, index) => (
            <Paper key={incident.id ?? index} variant="outlined" sx={{ p: 1.5, borderRadius: 1, bgcolor: '#fff' }}>
              <Typography sx={{ fontWeight: 900 }}>{incident.title ?? incident.incidentType ?? 'Incident'}</Typography>
              <Typography variant="body2" color="text.secondary">{formatDate(incident.incidentDate ?? incident.date ?? incident.createdAt)}</Typography>
            </Paper>
          ))}
        </>
      )}
    </Stack>
  );
}

export default function WorkerFieldWorkspacePage({ view, workerId, navigationBasePath = '/field' }: WorkerFieldWorkspacePageProps) {
  const navigate = useNavigate();
  const data = useWorkerFieldData({ workerId });
  const normalizedView = view === 'defects' || view === 'inspections' ? 'emergency' : view;

  if (!data.projectId) {
    return <Alert severity="warning">Select an active project to load this worker screen.</Alert>;
  }

  return (
    <Stack spacing={2.5}>
      <PageTitle data={data} title={
        normalizedView === 'home' ? 'Home screen'
          : normalizedView === 'current' ? 'Current task'
            : normalizedView === 'upcoming' ? 'Upcoming task'
              : normalizedView === 'performance' ? 'Performance history'
                : 'Safety & emergency'
      } />

      {data.error && <Alert severity="error">{data.error}</Alert>}
      {!data.workerLinked && !data.teamId && (
        <Alert severity="warning">
          This login is not linked to a worker or crew yet. Link the account before assigning real work.
        </Alert>
      )}
      {!data.workerLinked && data.teamId && normalizedView === 'performance' && (
        <Alert severity="warning">
          This login is not linked to a worker record by name yet. Showing crew-scoped records until the worker record is linked.
        </Alert>
      )}

      {normalizedView === 'home' && <HomeView data={data} navigationBasePath={navigationBasePath} />}
      {normalizedView === 'current' && (
        <CurrentTaskView
          tasks={data.tasks}
          taskResources={data.taskResources}
          defects={data.defects}
          reload={data.reload}
          onReturnToFieldMode={() => navigate(navigationBasePath)}
        />
      )}
      {normalizedView === 'upcoming' && <UpcomingView tasks={data.tasks} />}
      {normalizedView === 'performance' && <PerformanceView tasks={data.tasks} defects={data.defects} attendance={data.attendance} />}
      {normalizedView === 'emergency' && <EmergencyView tasks={data.tasks} contacts={data.contacts} incidents={data.incidents} toolboxTalks={data.toolboxTalks} />}
    </Stack>
  );
}
